import isEqual from 'fast-deep-equal';
import { type Draft, produce } from 'immer';

import { type EngineStateBase, type RuntimeState } from '@blaze-runtime/engine-data-access';
import {
  AUTO_QUERY_LOGICAL_NODE_ID,
  type CanvasElementId,
  type CanvasNamespaceId,
  CUSTOM_QUERY_LOGICAL_NODE_ID,
  getDataFilterInputName,
  getWorkflowInputNameForGeneratedField,
  isWorkflowInputGenerated,
  type RuntimeEventQueryData,
  SEARCH_AND_FILTERS_CONDITION_PREFIX,
  type WrapperConnectionData,
} from '@blaze-runtime/engine-domain';
import {
  getInputWorkflowIdForElement,
  getNamespaceId,
  getNewConnectionId,
} from '@blaze-runtime/engine-domain';
import {
  ComparisonOperator,
  type ComparisonQueryNode,
  type LogicalQueryNode,
  LogicalType,
  QueryNodeType,
} from '@blaze-shared/api-contracts-domain';
import {
  mapSimpleQueryConditionsToComplexFilters,
  removeEmptyLogicalNodes,
  removeNodes,
} from '@blaze-shared/datasets-domain';
import {
  type ChangePending,
  PaginationDirection,
  type RuntimePage,
} from '@blaze-shared/elements-domain';
import { type RuntimeEventType } from '@blaze-shared/schema-domain';
import {
  type Connection,
  findFirstNodeInWorkflowOfType,
  getAutoDisplayInfo,
  type GetWorkflowNode,
  isApiCallNode,
  isGetNode,
  MAIN_INPUT_NAME,
  type RuntimeElement,
  type Workflow,
  type WorkflowInputs,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

import {
  getConnectionsFrom,
  getConnectionsTo,
  sanitizeConnections,
} from '../../utils/runtime/connection';
import { getUiRuntimeElement, getWorkflowRuntimeElement } from '../../utils/runtime/runtime';

function getFilterConditionId(fieldRefId: string): string {
  return `${SEARCH_AND_FILTERS_CONDITION_PREFIX}${fieldRefId}`;
}

/**
 * Updates the state by mutating the connections collection
 * @returns the newly created connection
 */
export function addConnectionMutably(
  draft: Draft<RuntimeState>,
  ns: CanvasNamespaceId,
  source: RuntimeElement,
  eventType: RuntimeEventType,
  target: RuntimeElement,
): Connection {
  const id = getNewConnectionId(ns);
  const newConnection: Connection = { id, source, eventType, target };
  draft.connections.push(newConnection);
  return newConnection;
}

/**
 * Updates the connections and workflow in the state after Search & Filters or API Call parameters adjustments
 * @param assignments used for API Calls
 */
export function updateWrapperConnectionsMutably(
  draft: Draft<RuntimeState>,
  elementId: CanvasElementId,
  inputWorkflow: Workflow,
  connectionData: WrapperConnectionData[],
  restrictedWfInputNames: string[] | undefined,
): void {
  if (!inputWorkflow?.id) {
    throw new Error(`Could not find input workflow for element id ${elementId}`);
  }

  const getNode = findFirstNodeInWorkflowOfType(inputWorkflow, isGetNode);
  const apiCallNode = findFirstNodeInWorkflowOfType(inputWorkflow, isApiCallNode);

  const sourceNode = getNode || apiCallNode;

  if (!sourceNode) {
    throw new Error(`Source node missing in workflow ${inputWorkflow.id}`);
  }

  const ns = getNamespaceId(elementId);
  const inputWorkflowElement = getWorkflowRuntimeElement(inputWorkflow.id);

  // If could not find a input workflow - ensure the provided one is in the state and connected to the element
  if (!getInputWorkflowIdForElement(draft.connections, elementId)) {
    addOrUpdateWorkflowMutably(draft, inputWorkflow);
    addConnectionMutably(
      draft,
      ns,
      inputWorkflowElement,
      'data-ready',
      getUiRuntimeElement(elementId),
    );
  }

  const inputConnections = getConnectionsTo(
    draft.connections,
    getWorkflowRuntimeElement(inputWorkflow.id),
  );

  // Remove generated inputs
  const workflowInputs: WorkflowInputs = Object.entries(inputWorkflow.inputs || {})
    .filter(([inputName]) => !isWorkflowInputGenerated(inputName))
    .reduce((acc, [inputName, connectionId]) => {
      acc[inputName] = connectionId;
      return acc;
    }, {} as WorkflowInputs);
  const rootQueryNode: LogicalQueryNode = {
    nodeType: QueryNodeType.Logical,
    logicalType: LogicalType.And,
    children: [],
  };
  const searchAndFiltersNode: LogicalQueryNode = {
    nodeType: QueryNodeType.Logical,
    logicalType: LogicalType.And,
    children: [],
  };

  const searchNodes: LogicalQueryNode[] = [];
  const matchNodes: ComparisonQueryNode[] = [];
  const isDataFilter = restrictedWfInputNames !== undefined;
  // Ensure the provided elements are connected and add to workflow's input mapping
  connectionData.forEach((data) => {
    const sourceElement = getUiRuntimeElement(data.sourceElementId);
    let inputConnection = getConnectionsFrom(inputConnections, sourceElement).find(
      ({ eventType }) => eventType === data.eventType,
    );

    // If connection doesn't exist yet - add it
    if (!inputConnection) {
      inputConnection = addConnectionMutably(
        draft,
        ns,
        getUiRuntimeElement(data.sourceElementId),
        data.eventType,
        inputWorkflowElement,
      );
    }

    let inputName = getWorkflowInputNameForGeneratedField(data);
    if (!inputName) {
      return;
    }

    if (isDataFilter) {
      inputName = getDataFilterInputName(inputName);
    }
    // If inputName already used - find an unused one
    if (workflowInputs[inputName] || restrictedWfInputNames?.includes(inputName)) {
      for (let i = 2; ; i++) {
        const inputNameCandidate = `${inputName}_${i}`;
        if (
          !workflowInputs[inputNameCandidate] &&
          !restrictedWfInputNames?.includes(inputNameCandidate)
        ) {
          inputName = inputNameCandidate;
          break;
        }
      }
    }
    if (isDataFilter) {
      restrictedWfInputNames.push(inputName);
    }
    // Map connections
    workflowInputs[inputName] = inputConnection.id;

    // Fill in missing input1
    if (!isDataFilter) {
      const input1Connection = workflowInputs[MAIN_INPUT_NAME];
      if (!input1Connection || !draft.connections.find((c) => c.id === input1Connection)) {
        workflowInputs[MAIN_INPUT_NAME] = inputConnection.id;
      }
    }

    // Prepare query conditions
    if (data.filterType === 'search' && data.searchInFields && data.searchInFields.length > 0) {
      const displayInfo = getAutoDisplayInfo(inputName);
      const logicalQueryNode: LogicalQueryNode = {
        nodeType: QueryNodeType.Logical,
        logicalType: LogicalType.Or,
        children: data.searchInFields.map(
          (column, index) =>
            ({
              nodeType: QueryNodeType.Comparison,
              conditionId: getFilterConditionId(`${data.fieldRefIds[0]}${index}`),
              column,
              operator: ComparisonOperator.ContainsText,
              value: displayInfo.value,
              isFormulaValue: false,
              isOptional: true,
              displayInfos: [displayInfo],
            }) as ComparisonQueryNode,
        ),
      };
      searchNodes.push(logicalQueryNode);
    } else if (
      data.filterType === 'match' &&
      (data.fieldId || data.fieldName) &&
      data.filterOperator
    ) {
      const displayInfo = getAutoDisplayInfo(
        inputName,
        data.filterDataValueField,
        true,
        data.filterOperator === ComparisonOperator.In,
      );
      matchNodes.push({
        nodeType: QueryNodeType.Comparison,
        conditionId: getFilterConditionId(data.fieldRefIds[0]),
        column: data.fieldId || data.fieldName || '',
        operator: data.filterOperator,
        value: displayInfo.value,
        isFormulaValue: false,
        isOptional: true,
        displayInfos: [displayInfo],
      });
    } else if (
      data.filterType === 'ranged' &&
      (data.fieldId || data.fieldName) &&
      data.predefinedFilterOperators
    ) {
      const displayInfo = getAutoDisplayInfo(
        inputName,
        data.filterDataValueField,
        true,
        data.filterOperator === ComparisonOperator.In,
      );

      const startKey = data.fieldColumnDataType === 'DateTime' ? 'startDateTime' : 'startDate';
      const endKey = data.fieldColumnDataType === 'DateTime' ? 'endDateTime' : 'endDate';

      matchNodes.push(
        {
          nodeType: QueryNodeType.Comparison,
          conditionId: getFilterConditionId(data.fieldRefIds[0]),
          column: data.fieldId || data.fieldName || '',
          operator: ComparisonOperator.Gte,
          value: `${displayInfo.value}["${startKey}"]`,
          isFormulaValue: false,
          isOptional: true,
          displayInfos: [displayInfo],
        },
        {
          nodeType: QueryNodeType.Comparison,
          conditionId: getFilterConditionId(data.fieldRefIds[0]),
          column: data.fieldId || data.fieldName || '',
          operator: ComparisonOperator.Lte,
          value: `${displayInfo.value}["${endKey}"]`,
          isFormulaValue: false,
          isOptional: true,
          displayInfos: [displayInfo],
        },
      );
    }
  });

  if (searchAndFiltersNode.children.length) {
    rootQueryNode.children.push(searchAndFiltersNode);
  }

  // Prepare the updated workflow
  const updatedWorkflow = produce(inputWorkflow, (wfDraft) => {
    wfDraft.inputs = workflowInputs;

    // Identify the possible source nodes
    const apiCallNode = findFirstNodeInWorkflowOfType(wfDraft, isApiCallNode);
    const getNode = findFirstNodeInWorkflowOfType(wfDraft, isGetNode);
    if (!apiCallNode && !getNode) {
      throw new Error('Could not find source node');
    }

    if (getNode) {
      ensureSearchAndMatchQueriesMutable(getNode, searchNodes, matchNodes);
    }
    wfDraft.preventAutoStart = connectionData.length > 0;
  });

  updateWorkflowMutably(draft, updatedWorkflow);
  // Sanitize connections (some elements might have been removed when adjusting the wrapper)
  sanitizeConnectionsAndDataAfterUiChangeMutably(draft as Draft<EngineStateBase>);
}

export function ensureSearchAndMatchQueriesMutable(
  getNode: GetWorkflowNode,
  searchQueries: LogicalQueryNode[],
  matchQueries: ComparisonQueryNode[],
): void {
  const queryConditionsWithoutSearchAndFilters: (LogicalQueryNode | ComparisonQueryNode)[] = [];
  let queryLogicalOp = LogicalType.And;

  if (getNode.queryConditions?.length) {
    queryLogicalOp = getNode.queryLogicalOp ?? LogicalType.And;
    queryConditionsWithoutSearchAndFilters.push(
      ...mapSimpleQueryConditionsToComplexFilters(
        getNode.queryConditions.filter(
          (qc) => !qc.conditionId?.startsWith(SEARCH_AND_FILTERS_CONDITION_PREFIX),
        ),
        getNode.queryLogicalOp,
      ).children,
    );
    getNode.queryConditions = [];
  }

  if (!getNode.query) {
    getNode.query = {
      nodeType: QueryNodeType.Logical,
      logicalType: LogicalType.And,
      children: [
        {
          id: AUTO_QUERY_LOGICAL_NODE_ID,
          nodeType: QueryNodeType.Logical,
          logicalType: LogicalType.And,
          children: [...searchQueries, ...matchQueries],
        },
        {
          id: CUSTOM_QUERY_LOGICAL_NODE_ID,
          nodeType: QueryNodeType.Logical,
          logicalType: queryLogicalOp,
          children: queryConditionsWithoutSearchAndFilters,
        },
      ],
    };
    return;
  }

  getNode.query.children.push(...queryConditionsWithoutSearchAndFilters);

  removeNodes(
    getNode.query,
    (node) => node.nodeType === QueryNodeType.Logical && node.id === AUTO_QUERY_LOGICAL_NODE_ID,
  );

  // TODO - can be deleted when all search and filters are migrated to the new `AUTO_QUERY_LOGICAL_NODE_ID`
  removeNodes(
    getNode.query,
    (node) =>
      node.nodeType === QueryNodeType.Comparison &&
      !!node.conditionId?.startsWith(SEARCH_AND_FILTERS_CONDITION_PREFIX),
  );
  removeEmptyLogicalNodes(getNode.query);

  if (searchQueries.length || matchQueries.length) {
    if (
      !getNode.query.children.some(
        (node) =>
          node.nodeType === QueryNodeType.Logical && node.id === CUSTOM_QUERY_LOGICAL_NODE_ID,
      )
    ) {
      getNode.query = {
        nodeType: QueryNodeType.Logical,
        logicalType: LogicalType.And,
        children: [
          {
            id: CUSTOM_QUERY_LOGICAL_NODE_ID,
            nodeType: QueryNodeType.Logical,
            logicalType: queryLogicalOp,
            children: getNode.query.children,
          },
        ],
      };
    }

    getNode.query.children.push({
      id: AUTO_QUERY_LOGICAL_NODE_ID,
      nodeType: QueryNodeType.Logical,
      logicalType: LogicalType.And,
      children: [...searchQueries, ...matchQueries],
    });
  }
}

export function applyAiGeneratedWorkflowMutably(
  draft: Draft<RuntimeState>,
  workflow: Workflow,
  elementId: CanvasElementId,
  inputConnections: Connection[],
): void {
  const oldInputWorkflowId = getInputWorkflowIdForElement(draft.connections, elementId);
  // If input workflow already exists - disconnect it
  if (oldInputWorkflowId) {
    const oldWorkflowRuntimeElement = getWorkflowRuntimeElement(oldInputWorkflowId);
    // Clear connections to and from the old input workflow
    const connectionIdsToRemove = [
      ...getConnectionsTo(draft.connections, oldWorkflowRuntimeElement),
      ...getConnectionsFrom(draft.connections, oldWorkflowRuntimeElement),
    ].map(({ id }) => id);

    draft.connections = draft.connections.filter(({ id }) => !connectionIdsToRemove.includes(id));
  }

  // Add newly generated workflow
  addOrUpdateWorkflowMutably(draft, workflow);

  // Add input workflow connection to the element
  addConnectionMutably(
    draft,
    getNamespaceId(elementId),
    getWorkflowRuntimeElement(workflow.id),
    'data-ready',
    getUiRuntimeElement(elementId),
  );

  // Add input connections from related elements to the workflow
  inputConnections.forEach((connection) => addOrUpdateConnectionMutably(draft, connection));
}

export function removePendingUiMutably(
  draft: Draft<RuntimeState>,
  elementIds: CanvasElementId[],
  changePending: ChangePending,
): void {
  elementIds.forEach((id) => {
    draft.pendingUi[id] = (draft.pendingUi[id] || []).filter((change) => {
      if (change.cause === 'workflow' && changePending.cause === 'workflow') {
        return change.workflowId !== changePending.workflowId;
      } else if (change.cause === 'row-action' && changePending.cause === 'row-action') {
        return change.workflowId !== changePending.workflowId;
      } else if (
        change.cause !== 'row-action' &&
        changePending.cause !== 'row-action' &&
        change.cause !== 'workflow' &&
        changePending.cause !== 'workflow'
      ) {
        return change.datasetId !== changePending.datasetId;
      }

      return true;
    });
    if (draft.pendingUi[id].length === 0) {
      delete draft.pendingUi[id];
    }
  });
}

export function replaceConnectionsForNsMutably(
  draft: Draft<RuntimeState>,
  ns: CanvasNamespaceId,
  connections: Connection[],
): void {
  draft.connections = draft.connections.filter((connection) => !connection.id.startsWith(ns));
  draft.connections.push(...connections);
}

/**
 * Drops the editor's slot selection when the slot it points at is gone.
 *
 * The panel remembers which slot of a wizard or tabs element the builder has open. A
 * regeneration that removes slots leaves that selection dangling, and slot ids are recycled —
 * so a stale selection does not merely point at nothing, it can come to point at a *different*
 * step. Deriving it from the element's own slots is the same rule
 * `runtimeActions.updateElementMetaStepKill` applies per removed slot, without needing to be
 * told which slots those were.
 */
export function dropStaleSlotSelectionMutably(
  draft: Draft<EngineStateBase>,
  elementId: CanvasElementId,
): void {
  const elementsMeta = draft.elementsMeta[elementId];
  const dataFeatures = elementsMeta?.dataFeatures;
  const selectedSlotId =
    dataFeatures?.elementType === 'wizard' || dataFeatures?.elementType === 'tabs'
      ? dataFeatures.selectedSlotId
      : undefined;
  if (!elementsMeta || !selectedSlotId) {
    return;
  }
  const slotIds: string[] = draft.entities[elementId]?.slots?.ids ?? [];
  if (!slotIds.includes(selectedSlotId)) {
    delete elementsMeta.dataFeatures;
  }
}

/**
 * Mutably removes the connections to ui elements which are no longer present
 * @param draft the mutable state produced by immer
 * @param removedElementIds explicit list of removed element ids
 * @param currentElementIds the list of currently existing element ids (after removal)
 */
export function sanitizeConnectionsAndDataAfterUiChangeMutably(
  draft: Draft<EngineStateBase>,
  removedElementIds?: string[],
): void {
  const currentElementIds = draft.ids as CanvasElementId[];
  draft.connections = sanitizeConnections(draft.connections, currentElementIds);

  const effectiveRemovedElementsIds =
    removedElementIds ??
    Object.keys(draft.elementsMeta).filter((elementId) => !currentElementIds.includes(elementId));

  for (const elementId of effectiveRemovedElementsIds) {
    draft.initializedUiElements[elementId] = undefined;
    draft.elementsMeta[elementId] = undefined;

    // Legacy element schema might have schemas with the id `[elementId]` exactly,
    // new ones follow the `[elementId]__[eventType]` pattern
    const elementSchemaIdPrefix = `${elementId}__`;

    for (const outputKey in draft.outputsUi) {
      if (outputKey === elementId || outputKey.startsWith(elementSchemaIdPrefix)) {
        delete draft.outputsUi[outputKey];
      }
    }
    for (const schemaId in draft.schemas) {
      if (schemaId === elementId || schemaId.startsWith(elementSchemaIdPrefix)) {
        delete draft.schemas[schemaId];
      }
    }
  }
}

export function clearElementOutputsMutably(
  draft: Draft<RuntimeState>,
  pageId: CanvasNamespaceId,
): void {
  Object.keys(draft.outputsUi)
    .filter((key) => key.startsWith(`${pageId}_`)) // Element ids have format `${pageId}_{index}`
    .forEach((key) => {
      delete draft.outputsUi[key];
    });
}

export function addOrUpdateConnectionMutably(
  draft: Draft<RuntimeState>,
  connection: Connection,
): void {
  const index = draft.connections.findIndex((conn) => conn.id === connection.id);
  if (index === -1) {
    draft.connections.push(connection);
  } else {
    draft.connections[index] = connection;
  }
}

export function addOrUpdateWorkflowMutably(draft: Draft<RuntimeState>, workflow: Workflow): void {
  const index = draft.workflows.findIndex((wf) => wf.id === workflow.id);
  if (index === -1) {
    draft.workflows.push(workflow);
  } else {
    const workflowWasModified = !isEqual(draft.workflows[index], workflow);
    draft.workflows[index] = workflow;
    // Invalidate schema after update
    if (workflowWasModified) {
      delete draft.schemas[workflow.id];
    }
  }
}

export function updateWorkflowMutably(draft: Draft<RuntimeState>, workflow: Workflow): void {
  const index = draft.workflows.findIndex((wf) => wf.id === workflow.id);
  if (index === -1) {
    throw new Error(`Could not find workflow ID: ${workflow.id}`);
  }

  const workflowWasModified = !isEqual(draft.workflows[index], workflow);
  draft.workflows[index] = workflow;
  // Invalidate schema after update
  if (workflowWasModified) {
    delete draft.schemas[workflow.id];
  }
}

/** Ensures that a provided node has the provided nextNode */
export function ensureNodeHasNextNodeMutably(node: Draft<WorkflowNode>, nextNodeId: string): void {
  if (node.nextNodeId !== nextNodeId) {
    node.nextNodeId = nextNodeId;
  }
}

/**
 * Store query information for query type events. We store it in a separate place from eventsUi to make
 * sure we don't lose the information when ui element fires some other event in the meantime.
 * @param draft
 * @param queryEvent
 * @param elementId
 */
export function updateQueryStateMutably(
  draft: Draft<RuntimeState>,
  queryEvent: RuntimeEventQueryData,
  elementId: CanvasElementId,
): void {
  const query = draft.elementsQuery[elementId];

  if (!query) {
    return;
  }

  let requestedDirection = queryEvent.page?.direction;
  let pageIndex = query.pageIndex;

  if (!isEqual(query.sort, queryEvent.sort)) {
    requestedDirection = PaginationDirection.First;
  }

  if (requestedDirection) {
    draft.pendingPaginations[elementId] = {
      elementId,
      direction: requestedDirection,
    };

    if (
      requestedDirection === PaginationDirection.$Initial ||
      requestedDirection === PaginationDirection.First ||
      queryEvent.page?.index === 0
    ) {
      pageIndex = 0;
      query.currentPageToken = '';
    } else if (requestedDirection === PaginationDirection.Backwards) {
      pageIndex -= 1;
    } else if (requestedDirection === PaginationDirection.Forwards) {
      pageIndex += 1;
    }
  }

  query.pageIndex = pageIndex;
  query.pageSize = queryEvent.page?.size || query.pageSize;
  query.sort = queryEvent.sort;
  query.dataSource = queryEvent.dataSource;
  query.query = queryEvent.query;
  query.groups = queryEvent.groups;
  query.source = 'ui-event';
}

export function updateRuntimePagesMutably(
  draft: Draft<RuntimeState>,
  pages: { pageId: string; info: Partial<RuntimePage> }[],
): void {
  for (const { pageId, info } of pages) {
    draft.runtimePages[pageId] = { ...draft.runtimePages[pageId], ...(info as RuntimePage) };
  }
}

export function stopRuntime(state: EngineStateBase): EngineStateBase {
  return {
    ...state,
    connectionChains: {},
    isRunning: false,
    pendingUi: {},
    publishedUrl: '',
    workflowsStatus: {},
  };
}

export function getDataFilterWorkflowsInputNames(state: EngineStateBase): string[] {
  return Object.values(state.globalQuery).flatMap((q) => {
    const wfInputs = q?.workflowId
      ? (state.workflows.find((wf) => wf.id === q.workflowId)?.inputs ?? {})
      : {};
    return Object.keys(wfInputs);
  });
}
